export interface FloatingChatRuntimeConfig {
  apiUrl?: string;
  storagePrefix?: string;
  primaryColor?: string;
  primaryColorRgb?: string;
  fontFamily?: string;
  systemName?: string;
  systemAvatar?: string;
  visitorAvatar?: string;
}

type FloatingChatWindow = Window & {
  __FLOATING_CHAT_CONFIG__?: FloatingChatRuntimeConfig;
};

export interface FloatingChatStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

class MemoryFloatingChatStorage implements FloatingChatStorage {
  private readonly values = new Map<string, string>();

  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }
}

export function getFloatingChatRuntimeConfig(): FloatingChatRuntimeConfig {
  if (typeof window === 'undefined') {
    return {};
  }

  return (window as FloatingChatWindow).__FLOATING_CHAT_CONFIG__ ?? {};
}

export function createFloatingChatStorage(): FloatingChatStorage {
  if (typeof window === 'undefined') {
    return new MemoryFloatingChatStorage();
  }

  try {
    const storage = window.localStorage;
    const testKey = '__floating_chat_storage_test__';
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return storage;
  } catch {
    return new MemoryFloatingChatStorage();
  }
}

export function resolveFloatingChatRgbChannels(color: string): string {
  const normalized = color.trim();

  const hexMatch = normalized.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    const expanded = hex.length === 3
      ? hex.split('').map((char) => `${char}${char}`).join('')
      : hex;
    const red = Number.parseInt(expanded.slice(0, 2), 16);
    const green = Number.parseInt(expanded.slice(2, 4), 16);
    const blue = Number.parseInt(expanded.slice(4, 6), 16);
    return `${red}, ${green}, ${blue}`;
  }

  const rgbMatch = normalized.match(/^rgba?\(\s*([0-9.]+)[,\s]+([0-9.]+)[,\s]+([0-9.]+)(?:[,\s/]+[0-9.]+)?\s*\)$/i);
  if (rgbMatch) {
    const clamp = (value: string): number => {
      const parsed = Number.parseFloat(value);
      if (Number.isNaN(parsed)) {
        return 0;
      }
      return Math.max(0, Math.min(255, Math.round(parsed)));
    };

    return `${clamp(rgbMatch[1])}, ${clamp(rgbMatch[2])}, ${clamp(rgbMatch[3])}`;
  }

  return 'var(--primary-rgb)';
}
